import { useState } from 'react';
import { Button } from '../ui/Button';
import { Mail, Check, Sparkles, Gift, Bell, Heart } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const perks = [
  { icon: Gift, text: 'Exclusive subscriber discounts up to 30%' },
  { icon: Bell, text: 'New product launch alerts first' },
  { icon: Heart, text: 'Free weekly herbal wellness tips' },
  { icon: Sparkles, text: 'Early access to seasonal offers' },
];

export function Newsletter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  const { ref, inView } = useScrollAnimation();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setStatus('error');
      return;
    }
    setStatus('success');
    setEmail('');
    setTimeout(() => setStatus('idle'), 5000);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-primary-dark via-primary to-herbal-700 relative overflow-hidden">
      {/* Background patterns */}
      <div className="absolute inset-0 hero-pattern" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center scroll-hidden ${inView ? 'scroll-visible' : ''}`}
        >
          {/* Left */}
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 border border-white/20 text-white/90 rounded-full text-sm font-medium mb-6">
              <Mail className="w-4 h-4" />
              Newsletter
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              Stay Updated with<br />
              <span className="text-accent">Herbal Wellness</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-md">
              Join thousands of health-conscious Pakistanis. Get tips, discounts, and product news straight to your inbox.
            </p>
            <ul className="space-y-3">
              {perks.map(({ icon: Icon, text }, i) => (
                <li key={i} className="flex items-center gap-3 text-white/80">
                  <span className="w-8 h-8 bg-white/15 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-accent" />
                  </span>
                  <span className="text-sm">{text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right — form card */}
          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 shadow-2xl">
            {status === 'success' ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-300" />
                </div>
                <h3 className="text-white text-xl font-bold mb-2">You're subscribed! 🎉</h3>
                <p className="text-white/70 text-sm">Thank you! Check your email for a welcome discount.</p>
              </div>
            ) : (
              <>
                <h3 className="text-white text-xl font-bold mb-2">Get Exclusive Offers</h3>
                <p className="text-white/60 text-sm mb-6">No spam. Unsubscribe anytime.</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className={`w-full px-5 py-3.5 rounded-xl bg-white/15 border ${
                        status === 'error' ? 'border-red-400' : 'border-white/20'
                      } text-white placeholder-white/40 focus:outline-none focus:border-accent/70 focus:ring-2 focus:ring-accent/20 transition-all duration-200 text-sm`}
                    />
                    {status === 'error' && (
                      <p className="text-red-300 text-xs mt-1.5">Please enter a valid email address.</p>
                    )}
                  </div>

                  <Button type="submit" variant="accent" fullWidth size="lg">
                    Subscribe Now — It's Free!
                  </Button>
                </form>

                <div className="flex items-center gap-2 mt-6 pt-6 border-t border-white/10">
                  <div className="flex -space-x-2">
                    {['bg-green-400', 'bg-blue-400', 'bg-purple-400', 'bg-amber-400'].map((c, i) => (
                      <div key={i} className={`w-7 h-7 ${c} rounded-full border-2 border-white/20`} />
                    ))}
                  </div>
                  <p className="text-white/60 text-xs">
                    <span className="text-white font-semibold">5,200+</span> subscribers already joined
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}